import { useEffect, useRef, useState } from "react";

import Popper from "components/Popper";
import dispatchChartService from "api/dispatchChartService";
import { EParcelDispatchStage } from "dto/Mailroom/IDispatchChart";

import styles from "./dispatchBadge.module.scss";

const STAGE_CLASS: Record<number, string> = {
  [EParcelDispatchStage.ReadyToDispatch]: styles.stageReady,
  [EParcelDispatchStage.InChart]: styles.stageChart,
  [EParcelDispatchStage.Sent]: styles.stageSent,
  [EParcelDispatchStage.Received]: styles.stageReceived,
  [EParcelDispatchStage.Returned]: styles.stageReturned,
};

const stageClass = (stage: number) => STAGE_CLASS[stage] ?? styles.stageReady;

/**
 * Wskaźnik statusu przesyłek wygenerowanych z pisma (EditorDocument) w widoku Dysku.
 * Pokazuje główną sygnaturę wysyłki; klik → lista konkretnych przesyłek z etapem nadania.
 * Nic nie renderuje, gdy z pisma nie wygenerowano przesyłek.
 */
const DocumentDispatchBadge = ({ documentId }: { documentId: string }) => {
  const { data } = dispatchChartService.useGetDocumentParcels(documentId);
  const info = data?.data;

  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  // Własne zamykanie po kliknięciu poza (ClickAwayListener Poppera bywa przechwytywany
  // przez klik komórki DataGrid). Popup jest portalowany — sprawdzamy przez popupRef.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t)) return;
      if (popupRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  if (!info?.HasParcels) return null;

  const parcels = info.Parcels ?? [];
  // Etap zbiorczy = najmniej zaawansowana przesyłka (żeby rzucało się w oczy, co jeszcze wisi).
  const minStage = parcels.length
    ? Math.min(...parcels.map((p) => p.Stage))
    : EParcelDispatchStage.ReadyToDispatch;

  return (
    <div
      ref={ref}
      className={styles.wrap}
      onClick={(e) => {
        e.stopPropagation();
        setOpen(true);
      }}
    >
      <span
        className={`${styles.badge} ${stageClass(minStage)}`}
        title="Przesyłki wychodzące z pisma — kliknij, aby zobaczyć etapy"
      >
        {info.MajorSignature ?? "przesyłki"} · {parcels.length}
      </span>

      <Popper
        anchorEl={ref.current as HTMLElement}
        open={open}
        setOpen={setOpen}
        closeOnClickAway={false}
        overlay={false}
      >
        <div
          ref={popupRef}
          className={styles.popup}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.popupHead}>
            Przesyłki z pisma{info.MajorSignature ? ` · ${info.MajorSignature}` : ""}
          </div>
          <div className={styles.popupList}>
            {parcels.map((p) => (
              <div key={p.Id} className={styles.popupRow}>
                <span className={styles.pn}>{p.ParcelNumber ?? "—"}</span>
                <span className={styles.rn}>{p.ReceiverName ?? "—"}</span>
                {p.DeliveryKindName && (
                  <span className={styles.kind}>{p.DeliveryKindName}</span>
                )}
                <span className={`${styles.stage} ${stageClass(p.Stage)}`}>
                  {p.StageLabel}
                </span>
              </div>
            ))}
          </div>
        </div>
      </Popper>
    </div>
  );
};

export default DocumentDispatchBadge;
